import type { CSSProperties, ReactElement } from "react";
import { cn } from "@/lib/cn";

interface ToastProps {
  id: string;
  message: string;
  variant: "success" | "error" | "info";
  onDismiss: (id: string) => void;
}

const variantClasses: Record<ToastProps["variant"], string> = {
  success: "border-l-success-500 text-success-700",
  error: "border-l-danger-500 text-danger-700",
  info: "border-l-primary-400 text-primary-700",
};

function SuccessIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-success-500"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-danger-500"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg
      className="h-5 w-5 shrink-0 text-primary-400"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const icons: Record<ToastProps["variant"], ReactElement> = {
  success: <SuccessIcon />,
  error: <ErrorIcon />,
  info: <InfoIcon />,
};

const slideInStyle: CSSProperties = {
  animation: "toast-slide-in 0.2s ease-out forwards",
};

export function Toast({ id, message, variant, onDismiss }: ToastProps) {
  return (
    <>
      <style>{`
        @keyframes toast-slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        style={slideInStyle}
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-lg shadow-elevated border border-l-4",
          "bg-white",
          "border-surface-200",
          variantClasses[variant]
        )}
      >
        {icons[variant]}
        <p className="flex-1 text-sm font-medium text-surface-800">
          {message}
        </p>
        <button
          type="button"
          aria-label="Dismiss notification"
          onClick={() => onDismiss(id)}
          className={cn(
            "shrink-0 rounded p-0.5 text-surface-400 hover:text-surface-600",
            "transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
          )}
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M4.293 4.293a1 1 0 011.414 0L8 6.586l2.293-2.293a1 1 0 111.414 1.414L9.414 8l2.293 2.293a1 1 0 01-1.414 1.414L8 9.414l-2.293 2.293a1 1 0 01-1.414-1.414L6.586 8 4.293 5.707a1 1 0 010-1.414z" />
          </svg>
        </button>
      </div>
    </>
  );
}
